import {
  HttpClient,
  HttpErrorResponse,
  HttpHeaders,
} from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { environment } from '@environments/environment';
import { AnalyzeData } from '@shared/models/analyze';
import { Candidates } from '@shared/models/candidate';
import {
  AllProblems,
  Problem,
  ProblemStateResponse,
} from '@shared/models/problem';
import { TestCaseResult } from '@shared/models/test-case';
import {
  TestSet,
  TestSetItemModel,
  TestSetResponse,
} from '@shared/models/test-sets';
import { User } from '@shared/models/user';
import { catchError, firstValueFrom, map, throwError } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class ApiClient {
  private apiUrl = environment.apiUrl;
  private http = inject(HttpClient);

  /*
   * Authorization
   */
  async refreshAccessToken() {
    return firstValueFrom(
      this.http
        .get<{
          data: { newAccessToken: string };
        }>(`${this.apiUrl}/request-accesstoken`, { withCredentials: true })
        .pipe(
          map(res => res.data.newAccessToken),
          catchError(this.handleError)
        )
    );
  }

  async verifyAccessToken(accessToken: string) {
    return firstValueFrom(
      this.http
        .get<{ data: { message: string } }>(`${this.apiUrl}/verify`, {
          headers: { authorization: `Bearer ${accessToken}` },
        })
        .pipe(
          map(res => res.data.message),
          catchError(this.handleError)
        )
    );
  }

  async login(email: string, password: string) {
    return firstValueFrom(
      this.http
        .post<{
          data: {
            accessToken: string;
          };
        }>(
          `${this.apiUrl}/login`,
          { email, password },
          { withCredentials: true }
        )
        .pipe(catchError(this.handleError))
    );
  }

  async logout() {
    return firstValueFrom(
      this.http
        .post(`${this.apiUrl}/logout`, {}, { withCredentials: true })
        .pipe(catchError(this.handleError))
    );
  }
  async getGithubLoginUrl(): Promise<void> {
    window.location.href = `${this.apiUrl}/auth/github`;
  }

  async getGoogleLoginUrl(): Promise<void> {
    const dualScreenLeft =
      window.screenLeft !== undefined ? window.screenLeft : window.screenX;
    const dualScreenTop =
      window.screenTop !== undefined ? window.screenTop : window.screenY;

    const width = window.innerWidth
      ? window.innerWidth
      : document.documentElement.clientWidth
        ? document.documentElement.clientWidth
        : window.screen.width;
    const height = window.innerHeight
      ? window.innerHeight
      : document.documentElement.clientHeight
        ? document.documentElement.clientHeight
        : window.screen.height;

    const left = width / 2 - 250 + dualScreenLeft;
    const top = height / 2 - 300 + dualScreenTop;
    const redirectWindow = window.open(
      `${this.apiUrl}/auth/google`,
      '_blank',
      `toolbar=no, location=no, directories=no, status=no, menubar=no, scrollbars=no, resizable=no, copyhistory=no, width=500,height=600,left=${left},top=${top}`
    );

    const timer = setInterval(() => {
      if (redirectWindow!.closed) {
        clearInterval(timer);
        window.location.href = '/candidates';
      }
    }, 500);
  }

  /*
   * User Management
   */
  async getAllUsers(): Promise<User[]> {
    return firstValueFrom(
      this.http.get<User[]>(`${this.apiUrl}/users`).pipe(
        map(res => res),
        catchError(this.handleError)
      )
    );
  }

  async createUser(body: object) {
    return firstValueFrom(
      this.http
        .post(`${this.apiUrl}/users/create`, body)
        .pipe(catchError(this.handleError))
    );
  }

  async editUser(body: object, userId: string) {
    return firstValueFrom(
      this.http
        .patch(`${this.apiUrl}/users/update/${userId}`, body)
        .pipe(catchError(this.handleError))
    );
  }

  async deleteUser(userId: string) {
    return firstValueFrom(
      this.http
        .patch(`${this.apiUrl}/users/delete-user/${userId}`, {})
        .pipe(catchError(this.handleError))
    );
  }

  /*
   * Candidate Management
   */
  async getAllCandidates(): Promise<Candidates[]> {
    return firstValueFrom(
      this.http
        .get<{
          data: { candidates: Candidates[] };
        }>(`${this.apiUrl}/candidates`)
        .pipe(
          map(res => res.data.candidates),
          catchError(this.handleError)
        )
    );
  }

  async getAnalyzeById(id: string) {
    return firstValueFrom(
      this.http
        .get<{
          data: AnalyzeData;
        }>(`${this.apiUrl}/candidates/analyze/${id}`)
        .pipe(
          map(res => res.data),
          catchError(this.handleError)
        )
    );
  }

  async deleteCandidate(invitationId: string) {
    return firstValueFrom(
      this.http
        .delete<{ invitationId: string }>(`${this.apiUrl}/candidates`, {
          body: { invitationId },
        })
        .pipe(catchError(this.handleError))
    );
  }

  /*
   * Invitation Management
   */
  async sentEmail(body: object) {
    return firstValueFrom(
      this.http
        .post(`${this.apiUrl}/invitation`, body)
        .pipe(catchError(this.handleError))
    );
  }

  /*
   * Test Set Management
   */
  async getTestSets(): Promise<TestSetItemModel[]> {
    return firstValueFrom(
      this.http
        .get<{
          data: TestSetItemModel[];
        }>(`${this.apiUrl}/test-sets`)
        .pipe(
          map(res => res.data),
          catchError(this.handleError)
        )
    );
  }

  async getTestSetDataByTestSetId(testSetId: string) {
    return firstValueFrom(
      this.http
        .get<{
          data: TestSet;
        }>(`${this.apiUrl}/test-set/${testSetId}`)
        .pipe(
          map(res => res.data),
          catchError(this.handleError)
        )
    );
  }

  async createTestSets(body: object) {
    return firstValueFrom(
      this.http
        .post(`${this.apiUrl}/test-sets`, body)
        .pipe(catchError(this.handleError))
    );
  }

  async editTestSets(body: object) {
    return firstValueFrom(
      this.http
        .put(`${this.apiUrl}/test-sets`, body)
        .pipe(catchError(this.handleError))
    );
  }

  async deleteTestSets(testSetId: string) {
    return firstValueFrom(
      this.http
        .delete<{ testSetId: string }>(`${this.apiUrl}/test-set`, {
          body: { testSetId },
        })
        .pipe(catchError(this.handleError))
    );
  }

  async deleteTestSetProblem(body: object) {
    return firstValueFrom(
      this.http
        .delete(`${this.apiUrl}/test-sets/problem`, {
          body,
        })
        .pipe(catchError(this.handleError))
    );
  }

  /*
   * Problem Management
   */
  async getAllProblems(): Promise<AllProblems> {
    return firstValueFrom(
      this.http
        .get<{
          data: AllProblems;
        }>(`${this.apiUrl}/problems`)
        .pipe(
          map(res => res.data),
          catchError(this.handleError)
        )
    );
  }

  async getProblemDataById(id: string): Promise<Problem> {
    return firstValueFrom(
      this.http
        .get<{
          data: Problem;
        }>(`${this.apiUrl}/problem/data/${id}`)
        .pipe(
          map(res => res.data),
          catchError(this.handleError)
        )
    );
  }

  async createProblem(body: object) {
    return firstValueFrom(
      this.http
        .post(`${this.apiUrl}/problem/create`, body)
        .pipe(catchError(this.handleError))
    );
  }

  async updateProblem(body: object) {
    return firstValueFrom(
      this.http
        .put(`${this.apiUrl}/problems/`, body)
        .pipe(catchError(this.handleError))
    );
  }

  async deleteProblem(problemId: string) {
    return firstValueFrom(
      this.http
        .delete<{ problemId: string }>(`${this.apiUrl}/problem`, {
          body: { problemId },
        })
        .pipe(catchError(this.handleError))
    );
  }

  async verifyProblem(body: object): Promise<TestCaseResult[]> {
    return firstValueFrom(
      this.http
        .post<{
          data: {
            message: string;
            language: string;
            score: number;
            maxScore: number;
            submittedAt: string;
            testCaseResults: TestCaseResult[];
          };
        }>(`${this.apiUrl}/problem/verify`, body)
        .pipe(
          map(res => res.data.testCaseResults ?? []),
          catchError(this.handleError)
        )
    );
  }

  /*
   * Exam Management
   */
  async checkEmail(body: object) {
    return firstValueFrom(
      this.http
        .post(`${this.apiUrl}/invitation/check-email`, body)
        .pipe(catchError(this.handleError))
    );
  }

  async getProblemsByTestSet(
    id: string,
    invitedToken: string
  ): Promise<TestSetResponse> {
    return firstValueFrom(
      this.http
        .get<{ data: TestSetResponse }>(`${this.apiUrl}/problems/${id}`, {
          headers: {
            invitedToken: `${invitedToken}`,
          },
        })
        .pipe(
          map(res => res.data),
          catchError(this.handleError)
        )
    );
  }

  async getProblemById(id: string, invitedToken: string): Promise<Problem> {
    return firstValueFrom(
      this.http
        .get<{ data: Problem }>(`${this.apiUrl}/problem/${id}`, {
          headers: {
            authorization: `Bearer ${invitedToken}`,
          },
        })
        .pipe(
          map(res => res.data),
          catchError(this.handleError)
        )
    );
  }

  async runTest(header: string, body: object): Promise<TestCaseResult[]> {
    return firstValueFrom(
      this.http
        .post<{
          data: {
            message: string;
            language: string;
            score: number;
            maxScore: number;
            submittedAt: string;
            testCaseResults: TestCaseResult[];
          };
        }>(`${this.apiUrl}/problem/run-test`, body, {
          headers: {
            authorization: `Bearer ${header}`,
          },
        })
        .pipe(
          map(res => res.data.testCaseResults ?? []),
          catchError(this.handleError)
        )
    );
  }

  async submitTest(header: string, body: object): Promise<TestCaseResult[]> {
    return firstValueFrom(
      this.http
        .post<{
          data: { testCaseResults: TestCaseResult[] };
        }>(`${this.apiUrl}/problem/submit-test`, body, {
          headers: {
            authorization: `Bearer ${header}`,
          },
        })
        .pipe(
          map(res => res.data.testCaseResults ?? []),
          catchError(this.handleError)
        )
    );
  }

  async submission(body: object) {
    return firstValueFrom(this.http.patch(`${this.apiUrl}/submission`, body));
  }

  async problemsState(headers: HttpHeaders): Promise<ProblemStateResponse> {
    return firstValueFrom(
      this.http
        .get<{
          data: ProblemStateResponse;
        }>(`${this.apiUrl}/problems/get/state`, { headers: headers })
        .pipe(
          map(res => res.data),
          catchError(this.handleError)
        )
    );
  }

  /*
   * Error Handling
   */
  private handleError(error: HttpErrorResponse) {
    if (error.status === 0) {
      console.error('An error occurred');
    } else {
      console.error(`Backend returned code ${error.status}`);
    }
    return throwError(() => error);
  }
}
